import type { LongTermGoalInput, TaskInput } from "@/types";
import { shortDate } from "@/lib/date";
import type { PlanView, TaskRecord } from "./dashboard";
import {
  type TimeWindow,
  chartWindow,
  goalBar,
  markerPct,
  monthTicks,
  nowMarkerPct,
  taskBar,
} from "./ganttScale";

const NO_DATES = "No dates";
const NO_STEPS = "No steps yet.";
const GOAL_ROW_LABEL = "Goal";

const STATUS_LABEL: Record<TaskInput["status"], string> = {
  planned: "Planned",
  inProgress: "In progress",
  completed: "Completed",
  failed: "Failed",
  delayed: "Delayed",
};

type PlanGanttProps = PlanView & { goal: LongTermGoalInput };

/**
 * A plan and its steps as a compact Gantt chart. The axis spans the goal's own
 * window, so the steps are read against the deadline they exist to serve.
 */
export function PlanGantt({ goal, plan, tasks }: PlanGanttProps) {
  const { title, startAt, endAt } = plan.fields;
  const window = chartWindow(
    goal,
    plan.fields,
    tasks.map((task) => task.fields),
  );

  return (
    <div className="plan">
      <div className="plan-head">
        <span className="plan-title">{title}</span>
        <span className="plan-dates">
          {shortDate(startAt)} – {shortDate(endAt)}
        </span>
      </div>
      {tasks.length === 0 || window === null ? (
        <p className="card-hint">{NO_STEPS}</p>
      ) : (
        <Chart window={window} goal={goal} tasks={tasks} />
      )}
    </div>
  );
}

function Chart({
  window,
  goal,
  tasks,
}: {
  window: TimeWindow;
  goal: LongTermGoalInput;
  tasks: TaskRecord[];
}) {
  const ticks = monthTicks(window);
  const todayPct = nowMarkerPct(window, Date.now());
  const goalStartPct = markerPct(window, goal.startAt);
  const goalDeadlinePct = markerPct(window, goal.deadlineAt);

  return (
    <div className="gantt">
      <div className="gantt-scale" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick.leftPct} className="gantt-tick" style={{ left: `${tick.leftPct}%` }}>
            {tick.label}
          </span>
        ))}
      </div>
      {/* Every row shares this axis, so the goal's boundaries and today's date
       * are drawn once, spanning the whole body. */}
      <div className="gantt-body">
        {goalStartPct !== null && (
          <span className="gantt-edge" style={{ left: `${goalStartPct}%` }} aria-hidden="true" />
        )}
        {goalDeadlinePct !== null && (
          <span className="gantt-edge" style={{ left: `${goalDeadlinePct}%` }} aria-hidden="true" />
        )}
        {todayPct !== null && (
          <span className="gantt-today" style={{ left: `${todayPct}%` }} aria-hidden="true" />
        )}
        <GoalRow window={window} goal={goal} />
        {tasks.map((task) => (
          <TaskRow key={task.id} window={window} task={task} />
        ))}
      </div>
    </div>
  );
}

/** The goal's own span, as the chart's summary band above its steps. */
function GoalRow({ window, goal }: { window: TimeWindow; goal: LongTermGoalInput }) {
  const bar = goalBar(window, goal);
  if (bar === null) return null;

  const span = `${shortDate(goal.startAt)} – ${shortDate(goal.deadlineAt)}`;

  return (
    <div className="gantt-row is-goal">
      <span className="gantt-label is-goal-label">
        {GOAL_ROW_LABEL} · {goal.title}
      </span>
      <div className="gantt-track">
        <span
          className="gantt-bar is-goal-bar"
          style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
          aria-label={`${goal.title}: ${span}`}
        />
      </div>
      {/* The goal's dates, pinned to the ends of its own band. */}
      <div className="gantt-edge-dates" aria-hidden="true">
        <span className="gantt-edge-date" style={{ left: `${bar.leftPct}%` }}>
          {shortDate(goal.startAt)}
        </span>
        <span
          className="gantt-edge-date is-end"
          style={{ left: `${bar.leftPct + bar.widthPct}%` }}
        >
          {shortDate(goal.deadlineAt)}
        </span>
      </div>
    </div>
  );
}

function TaskRow({ window, task }: { window: TimeWindow; task: TaskRecord }) {
  const { title, status, plannedStartAt, plannedEndAt } = task.fields;
  const bar = taskBar(window, task.fields);
  const span =
    plannedStartAt && plannedEndAt
      ? `${shortDate(plannedStartAt)} – ${shortDate(plannedEndAt)}`
      : NO_DATES;

  return (
    <div className="gantt-row">
      <span className="gantt-label" title={`${title} — ${STATUS_LABEL[status]}, ${span}`}>
        {title}
      </span>
      <div className="gantt-track">
        {bar === null ? (
          <span className="gantt-undated">{NO_DATES}</span>
        ) : (
          <span
            className="gantt-bar"
            data-status={status}
            style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
            aria-label={`${title}: ${STATUS_LABEL[status]}, ${span}`}
          />
        )}
      </div>
    </div>
  );
}
